<template>
  <div class="okr-funnel-chart">
    <svg
      :viewBox="`0 0 300 ${svgH}`"
      preserveAspectRatio="xMidYMid meet"
      class="funnel-svg"
    >
      <polygon
        v-for="(stage, index) in stages"
        :key="stage.key"
        :points="stagePoints(index)"
        :fill="colors[index]"
        class="funnel-polygon"
        :style="{ animationDelay: `${index * 80}ms` }"
      />
      <polygon
        :points="tipPoints"
        :fill="colors[colors.length - 1]"
        class="funnel-polygon"
        :style="{ animationDelay: `${stages.length * 80}ms` }"
      />
      <text
        v-for="(stage, index) in stages"
        :key="`lbl-${stage.key}`"
        x="150"
        :y="stageLabelY(index)"
        text-anchor="middle"
        dominant-baseline="central"
        class="funnel-text"
        :style="{ animationDelay: `${index * 80 + 60}ms` }"
      ><tspan class="funnel-tspan-name">{{ stage.label }}</tspan><tspan dx="8" class="funnel-tspan-value">{{ stage.value }}</tspan></text>
    </svg>

    <div v-if="mode === 'realized'" class="funnel-mode-badge">
      Realizado
    </div>
    <div v-else class="funnel-mode-badge planned">
      Planejado
    </div>
  </div>
</template>

<script>
const STAGE_COLORS_PLANNED = [
  'rgba(0, 82, 204, 0.22)',
  'rgba(0, 82, 204, 0.38)',
  'rgba(0, 82, 204, 0.54)',
  'rgba(0, 82, 204, 0.70)',
  'rgba(0, 82, 204, 0.86)',
]
const STAGE_COLORS_REALIZED = [
  'rgba(40, 199, 111, 0.22)',
  'rgba(40, 199, 111, 0.38)',
  'rgba(40, 199, 111, 0.54)',
  'rgba(40, 199, 111, 0.70)',
  'rgba(40, 199, 111, 0.86)',
]
const SVG_W = 300
const STAGE_H = 52
const TIP_H = 20
const TIP_NECK_PCT = 8

export default {
  name: 'OkrFunnelChart',

  props: {
    planned:  { type: Object, default: () => ({}) },
    realized: { type: Object, default: () => ({}) },
    mode:     { type: String, default: 'planned' },
    // escala comum entre os funis (0 = escala própria)
    scaleMax: { type: Number, default: 0 },
  },

  computed: {
    source() {
      return (this.mode === 'realized' ? this.realized : this.planned) || {}
    },

    stages() {
      return [
        { key: 'leads_quantity',     label: 'Leads',         value: this.source.leads_quantity     ?? 0 },
        { key: 'hot_leads_quantity', label: 'Leads quentes', value: this.source.hot_leads_quantity ?? 0 },
        { key: 'ea_quantity',        label: 'EA',            value: this.source.ea_quantity        ?? 0 },
        { key: 'ef_quantity',        label: 'EF',            value: this.source.ef_quantity        ?? 0 },
        { key: 'ep_quantity',        label: 'EP',            value: this.source.ep_quantity        ?? 0 },
      ]
    },

    maxValue() {
      if (this.scaleMax > 0) return this.scaleMax
      return Math.max(...this.stages.map(s => s.value), 1)
    },

    colors() {
      return this.mode === 'realized' ? STAGE_COLORS_REALIZED : STAGE_COLORS_PLANNED
    },

    svgH() {
      return this.stages.length * STAGE_H + TIP_H
    },

    tipPoints() {
      const yTop = this.stages.length * STAGE_H
      const neckPct = Math.min(TIP_NECK_PCT, this.widthPct(this.stages.length - 1))
      const wTop = neckPct / 100 * SVG_W
      const cx = SVG_W / 2
      return `${cx - wTop / 2},${yTop} ${cx + wTop / 2},${yTop} ${cx},${yTop + TIP_H}`
    },
  },

  methods: {
    widthPct(index) {
      const v = this.stages[index].value
      const maxV = this.maxValue
      if (v <= 0) return TIP_NECK_PCT
      // sqrt scale: preserves visible differences between small values
      return Math.max(TIP_NECK_PCT + 2, Math.round(Math.sqrt(v) / Math.sqrt(maxV) * 100))
    },

    stagePoints(index) {
      const cx = SVG_W / 2
      const wTop = this.widthPct(index) / 100 * SVG_W
      const neckPct = Math.min(TIP_NECK_PCT, this.widthPct(this.stages.length - 1))
      const wBot = (index < this.stages.length - 1 ? this.widthPct(index + 1) : neckPct) / 100 * SVG_W
      const yTop = index * STAGE_H
      const yBot = yTop + STAGE_H
      return [
        `${cx - wTop / 2},${yTop}`,
        `${cx + wTop / 2},${yTop}`,
        `${cx + wBot / 2},${yBot}`,
        `${cx - wBot / 2},${yBot}`,
      ].join(' ')
    },

    stageLabelY(index) {
      return index * STAGE_H + STAGE_H / 2
    },
  },
}
</script>

<style scoped>
.okr-funnel-chart {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;
}
.funnel-svg {
  width: 100%;
  height: auto;
  overflow: visible;
}
.funnel-polygon {
  animation: funnelIn 0.35s ease both;
  transform-box: fill-box;
  transform-origin: center;
}
@keyframes funnelIn {
  from { opacity: 0; transform: scaleX(0.82); }
  to   { opacity: 1; transform: scaleX(1); }
}
.funnel-text {
  animation: labelIn 0.25s ease both;
  pointer-events: none;
}
@keyframes labelIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.funnel-tspan-name {
  font-size: 11px;
  font-weight: 600;
  fill: #4b4b6b;
}
.funnel-tspan-value {
  font-size: 13px;
  font-weight: 700;
  fill: #1e1e3a;
}
.funnel-mode-badge {
  align-self: center;
  margin-top: 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #28c76f;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.funnel-mode-badge.planned {
  color: #0052cc;
}
</style>

<style>
.dark-layout .okr-funnel-chart .funnel-tspan-name { fill: #c2c2d6; }
.dark-layout .okr-funnel-chart .funnel-tspan-value { fill: #e8e8f0; }
</style>
